import { useCallback, useEffect, useState } from 'react';
import { isAbortError, toZwApiError, type ZwApiError } from '../api/client';
import type { EventTypePublic } from '../api/types';
import { useZeitwahl } from '../provider';

export interface UseEventTypeOptions {
  locale?: string;
  brand?: string;
  enabled?: boolean;
}

export interface UseEventTypeResult {
  data: EventTypePublic | null;
  error: ZwApiError | null;
  loading: boolean;
  reload: () => void;
}

export function useEventType(
  org: string,
  slug: string,
  options: UseEventTypeOptions = {}
): UseEventTypeResult {
  const { api, locale: contextLocale } = useZeitwahl();
  const { brand, enabled = true } = options;
  const locale = options.locale ?? contextLocale;
  const [state, setState] = useState<Omit<UseEventTypeResult, 'reload'>>({
    data: null,
    error: null,
    loading: enabled,
  });
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    setState((s) => ({ ...s, loading: true, error: null }));
    api.getEventType(org, slug, { locale, brand }, { signal: controller.signal }).then(
      (data) => {
        if (!controller.signal.aborted) setState({ data, error: null, loading: false });
      },
      (error: unknown) => {
        if (controller.signal.aborted || isAbortError(error)) return;
        setState({ data: null, error: toZwApiError(error), loading: false });
      }
    );
    return () => controller.abort();
  }, [api, org, slug, locale, brand, enabled, version]);

  const reload = useCallback(() => setVersion((v) => v + 1), []);
  return { ...state, reload };
}
